/**
 * Safari (WebKit) drops the IndexedDB connection of a page or Home-Screen app it suspends in the
 * background; the next request on that connection fails — "UnknownError: Unable to open cursor",
 * "UnknownError: Connection to Indexed Database server lost", "InvalidStateError: The database
 * connection is closing" (Dexie wraps them, keeping the browser's error as `inner`). Nothing was
 * written by the failed transaction, and reopening the database fixes it, so the local databases
 * (annotations, outbox, offline packs) reopen once and run the same operation again (pure parts
 * unit tested).
 */

const LOST_MESSAGE = /Unable to open cursor|Indexed Database server lost|connection is closing|database connection is closed/i;

/** The error means the IndexedDB connection is gone (reopen and retry), not that the request itself is wrong. */
export function isLostIdbConnection(e: unknown, depth = 0): boolean {
  if (!e || typeof e !== 'object' || depth > 3) return false;
  const err = e as { name?: unknown; message?: unknown; inner?: unknown };
  const name = typeof err.name === 'string' ? err.name : '';
  const message = typeof err.message === 'string' ? err.message : '';
  if (name === 'DatabaseClosedError' || name === 'UnknownError' || LOST_MESSAGE.test(message)) return true;
  return isLostIdbConnection(err.inner, depth + 1);
}

/** A Dexie database as far as reopening goes. */
export interface Reopenable {
  close(): void;
  open(): Promise<unknown>;
}

/**
 * Runs `op`; when it fails because the connection was lost, closes and reopens `db` and runs `op`
 * once more (a second failure is the caller's). Other errors pass through untouched.
 */
export async function withReopen<T>(db: Reopenable, op: () => Promise<T>): Promise<T> {
  try {
    return await op();
  } catch (e) {
    if (!isLostIdbConnection(e)) throw e;
    try {
      db.close();
    } catch {
      /* already closed */
    }
    await db.open();
    return op();
  }
}
